import { useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useActionHistoryStore } from '@/stores/actionHistoryStore';
import { useUIStore } from '@/stores/uiStore';

export interface ShortcutConfig {
  key: string;
  ctrl?: boolean;
  meta?: boolean;
  shift?: boolean;
  alt?: boolean;
  action: () => void;
  description: string;
  category: 'navigation' | 'actions' | 'general';
  global?: boolean; // If true, works even in input fields
}

interface UseKeyboardShortcutsOptions {
  enabled?: boolean;
  onCommandPaletteToggle?: () => void;
  onNewProduct?: () => void;
  onVoiceInput?: () => void;
  onExport?: () => void;
  onShortcutsHelp?: () => void;
}

export function useKeyboardShortcuts(options: UseKeyboardShortcutsOptions = {}) {
  const {
    enabled = true,
    onCommandPaletteToggle,
    onNewProduct,
    onVoiceInput,
    onExport,
    onShortcutsHelp,
  } = options;

  const navigate = useNavigate();
  const { undo, redo, canUndo, canRedo } = useActionHistoryStore();
  const { addToast } = useUIStore();

  const handleUndo = useCallback(async () => {
    if (canUndo()) {
      try {
        await undo();
        addToast({ title: 'Action annulée', variant: 'success' });
      } catch {
        addToast({ title: 'Erreur lors de l\'annulation', variant: 'destructive' });
      }
    }
  }, [undo, canUndo, addToast]);

  const handleRedo = useCallback(async () => {
    if (canRedo()) {
      try {
        await redo();
        addToast({ title: 'Action rétablie', variant: 'success' });
      } catch {
        addToast({ title: 'Erreur lors du rétablissement', variant: 'destructive' });
      }
    }
  }, [redo, canRedo, addToast]);

  const shortcuts = useMemo<ShortcutConfig[]>(() => [
    // General
    {
      key: 'k',
      ctrl: true,
      action: () => onCommandPaletteToggle?.(),
      description: 'Recherche globale',
      category: 'general',
      global: true,
    },
    {
      key: '/',
      action: () => onShortcutsHelp?.(),
      description: 'Aide raccourcis',
      category: 'general',
    },

    // Navigation
    {
      key: '1',
      ctrl: true,
      action: () => navigate('/dashboard'),
      description: 'Dashboard',
      category: 'navigation',
    },
    {
      key: '2',
      ctrl: true,
      action: () => navigate('/inventory'),
      description: 'Inventaire',
      category: 'navigation',
    },
    {
      key: '3',
      ctrl: true,
      action: () => navigate('/products'),
      description: 'Produits',
      category: 'navigation',
    },
    {
      key: '4',
      ctrl: true,
      action: () => navigate('/alerts'),
      description: 'Alertes',
      category: 'navigation',
    },
    {
      key: '5',
      ctrl: true,
      action: () => navigate('/history'),
      description: 'Historique',
      category: 'navigation',
    },

    // Actions
    {
      key: 'n',
      ctrl: true,
      action: () => onNewProduct?.(),
      description: 'Nouveau produit',
      category: 'actions',
    },
    {
      key: 'v',
      ctrl: true,
      shift: true,
      action: () => onVoiceInput?.(),
      description: 'Saisie vocale',
      category: 'actions',
    },
    {
      key: 'e',
      ctrl: true,
      action: () => onExport?.(),
      description: 'Exporter',
      category: 'actions',
    },
    {
      key: 'z',
      ctrl: true,
      action: handleUndo,
      description: 'Annuler',
      category: 'actions',
    },
    {
      key: 'z',
      ctrl: true,
      shift: true,
      action: handleRedo,
      description: 'Rétablir',
      category: 'actions',
    },
    {
      key: 'y',
      ctrl: true,
      action: handleRedo,
      description: 'Rétablir',
      category: 'actions',
    },
  ], [navigate, handleUndo, handleRedo, onCommandPaletteToggle, onNewProduct, onVoiceInput, onExport, onShortcutsHelp]);

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if we're in an input field
      const target = e.target as HTMLElement;
      const isInInput = ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) ||
        target.isContentEditable;

      // Find matching shortcut
      const shortcut = shortcuts.find((s) => {
        // Check key
        if (s.key.toLowerCase() !== e.key.toLowerCase()) return false;

        // Check modifiers (ctrl or meta for Mac compatibility)
        const ctrlOrMeta = e.ctrlKey || e.metaKey;
        if (s.ctrl && !ctrlOrMeta) return false;
        if (!s.ctrl && !s.meta && ctrlOrMeta && s.key !== '/') return false;

        if (s.shift && !e.shiftKey) return false;
        if (!s.shift && e.shiftKey && s.key !== '/') return false;

        if (s.alt && !e.altKey) return false;
        if (!s.alt && e.altKey) return false;

        return true;
      });

      if (!shortcut) return;

      // Skip if in input field and shortcut is not global
      if (isInInput && !shortcut.global) {
        // Allow undo/redo and command palette even in inputs
        if (!['z', 'y', 'k'].includes(shortcut.key.toLowerCase())) {
          return;
        }
      }

      e.preventDefault();
      shortcut.action();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [enabled, shortcuts]);

  return {
    shortcuts,
    shortcutsByCategory: useMemo(() => ({
      navigation: shortcuts.filter(s => s.category === 'navigation'),
      actions: shortcuts.filter(s => s.category === 'actions'),
      general: shortcuts.filter(s => s.category === 'general'),
    }), [shortcuts]),
  };
}

// Helper to format shortcut for display
export function formatShortcut(shortcut: ShortcutConfig): string {
  const parts: string[] = [];

  if (shortcut.ctrl) {
    // Use Cmd on Mac, Ctrl otherwise
    const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.userAgent);
    parts.push(isMac ? '⌘' : 'Ctrl');
  }

  if (shortcut.shift) parts.push('Shift');
  if (shortcut.alt) parts.push('Alt');

  // Format key
  let key = shortcut.key.toUpperCase();
  if (key === '/') key = '?';

  parts.push(key);

  return parts.join('+');
}
